<script setup lang="ts">
import { computed } from "vue"
import { type RouteLocationResolved, useRoute } from "vue-router"
import SvgIcon from "@jamescoyle/vue-icon"
import NFansLogoInvert from "../../components/NFansLogoInvert.vue"
import { buildNavigationItems } from "./navigation"
import { useAppContext } from "../../AppContext"
import { badgeFor } from "../../community/composables/useActivityIndicator"

const route = useRoute()
const items = computed(() => buildNavigationItems())
const { communityIndicator } = useAppContext()
const communityBadge = badgeFor(communityIndicator)

function isParentOf(resolved: RouteLocationResolved): boolean {
  return route.fullPath.startsWith(resolved.path + "/")
}
</script>

<template>
  <aside class="w-64 shrink-0 bg-neutral text-neutral-content">
    <div class="flex flex-col h-full">
      <div class="h-16 flex items-center justify-center px-4 shrink-0">
        <NFansLogoInvert
          class="h-8 w-auto"
          alt="NFans"
          data-testid="app-sidebar-logo"
        />
      </div>
      <nav
        aria-label="Hauptnavigation"
        class="flex-1 overflow-y-auto"
      >
        <!--
          DaisyUI's menu active/hover colors assume a light surface; this sidebar
          uses bg-neutral, so the active background (var(--color-neutral)) blends
          into it. Remap the active tokens to primary and give hover a light overlay.
        -->
        <ul
          class="menu menu-vertical w-full gap-1 px-2"
          style="--menu-active-bg: var(--color-primary); --menu-active-fg: var(--color-primary-content);"
        >
          <li
            v-for="item in items"
            :key="item.routeName"
          >
            <router-link
              v-slot="{ href, route: linkRoute, navigate, isActive }"
              :to="item.to"
              custom
            >
              <a
                :href="href"
                :class="{ 'menu-active': isActive || isParentOf(linkRoute) }"
                :aria-current="(isActive || isParentOf(linkRoute)) ? 'page' : undefined"
                class="flex items-center gap-3 hover:bg-white/10"
                @click="navigate"
              >
                <span
                  v-if="item.routeName === 'community' && communityBadge"
                  class="indicator"
                >
                  <span
                    class="indicator-item badge badge-sm"
                    :class="communityBadge.color === 'error' ? 'badge-error' : 'badge-success'"
                    data-testid="sidebar-community-badge"
                  >{{ communityBadge.count }}</span>
                  <svg-icon
                    type="mdi"
                    :path="item.icon"
                    class="h-5 w-5 shrink-0"
                    aria-hidden="true"
                  />
                </span>
                <svg-icon
                  v-else
                  type="mdi"
                  :path="item.icon"
                  class="h-5 w-5 shrink-0"
                  aria-hidden="true"
                />
                <span class="truncate">{{ item.name }}</span>
              </a>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>
  </aside>
</template>
